import React, { FunctionComponent, useCallback, useEffect, useState, useMemo, ReactNode } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminDocument,
  AdminProductDetail,
  AdminProductOverview,
  ChangeTypeEnum,
  Money,
  NetworkNameTechnicalEnum,
  ProductTypeEnum,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons, makeModalForm } from 'ui/molecules/modal';
import * as Styled from './styled';
import Icon from 'src/ui/atoms/icon';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import DatePicker from 'src/ui/atoms/date-picker';
import SelectAtom from 'src/ui/atoms/select';
import useTranslate, { useTranslateWithStringArgs } from 'ui/hooks/use-translate';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import { ActionButton } from 'src/ui/molecules/action-buttons';
import { handleError } from 'src/ui/helper/error-handling';
import AddProductInfo from './add-product-info';
import AddProductExit from './add-product-exit';
import { snakeToCamelCaseWithNumbers } from 'ui/helper/case-transforms';
import ServerError from 'ui/types/server-error';
import { toNumber } from 'src/ui/helper/money';
import { FileWithDocType } from 'ui/atoms/file-upload';
import { debounce } from 'lodash';
import Section from 'src/ui/atoms/section';
import Header from 'src/ui/atoms/header';
import AddProductSuccess from './add-product-success';
import { getNetworkType, getWalletAddressForNetworkAssertExistence } from 'src/ui/helper/networks';
import Hint from 'src/ui/atoms/hint';
import { validateWalletAddress } from 'src/ui/helper/validators';
import FileUploadDocumentContainer from 'src/ui/atoms/file-upload/file-upload-document-container';
import Spacer from 'src/ui/atoms/spacer';

const isStellarNetwork = (network?: NetworkNameTechnicalEnum): boolean => {
  if (!network) return false;
  return network.includes('stellar');
};

interface AddProductModalProps {
  onSubmit: (fieldsValues: StateValues<AddProductFields>, dateAndTime?: Date) => void;
  onClose: () => void;
  productCreated?: AdminProductDetail;
  isInfoView: boolean;
  isExitingView: boolean;
  isSuccessView?: boolean;
  modalHeader: ReactNode;
  actionButtons: ActionButton[];
  error?: ServerError;
  data?: Omit<AdminProductOverview, 'network'> & AdminProductDetail;
  canEditProduct?: boolean;
  canEditIssuanceDocuments?: boolean;
  isProductValidated?: boolean;
  isProductUnderValidation?: boolean;
}

export interface AddProductFields {
  issuerId: string;
  issuerName?: string;
  name: string;
  issuerWalletAddress: string;
  productType: ProductTypeEnum;
  ewpg: string;
  publicationRestricted: string;
  isin: string;
  network: NetworkNameTechnicalEnum;
  assetCode: string;
  calculationFactor: string | null;
  backupWallet: string;
  nominalValue: number | Money | null;
  issuancePrice: number | Money | null;
  agioPercent: string;
  stepSize: number;
  maxIssuableAmount: number;
  paymentDeadline: number;
  cancellationDeadline: number;
  issuanceDocument: FileWithDocType;
  changeType?: ChangeTypeEnum;
  lockInTime?: Date | null;
  provideNominalValue: string;
}

export const Form = makeModalForm<AddProductFields>();

const AddProductModal: FunctionComponent<AddProductModalProps> = (props) => {
  const {
    isExitingView,
    isInfoView,
    isSuccessView,
    onClose,
    onSubmit,
    modalHeader,
    actionButtons,
    error,
    data,
    canEditProduct,
    canEditIssuanceDocuments,
    isProductValidated,
    isProductUnderValidation,
  } = props;
  const [issuers, setIssuers] = useState<{ label: string; value: string }[]>([]);
  const [time] = useState(['now', 'midnight', 'morning', 'noon', 'evening']);
  const [dateValue, setDateValue] = useState<string>('');
  const [timeValue, setTimeValue] = useState<string>('');
  const [dateAndTime, setDateAndTime] = useState<Date | undefined>(undefined);
  const [isEwpg, setIsEwpg] = useState(data?.ewpg || false);
  const [provideNominalValue, setProvideNominalValue] = useState(data ? data.provideNominalValue : true);
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkNameTechnicalEnum | undefined>(
    data?.network || undefined,
  );
  const [issuerWalletAddress, setIssuerWalletAddress] = useState('');
  const [distributionPlatform, setDistributionPlatform] = useState('');
  const [uploadedIssuanceDocument, setUploadedIssuanceDocument] = useState<AdminDocument | null>(
    data?.issuanceDocument?.document || null,
  );

  const { makeAuthenticatedApi, withApi } = useApiCall();

  const translate = useTranslateWithStringArgs();
  const isPhone = useIsMedia(DEVICES.phone);

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  useEffect(() => {
    if (dateValue.length === 0) return;

    let newDate = new Date(dateValue);
    const currentTime = new Date();

    switch (timeValue) {
      case 'now':
        newDate.setHours(currentTime.getHours(), 0, 0, 0);
        break;
      case 'midnight':
        newDate.setHours(0, 0, 0, 0);
        break;
      case 'morning':
        newDate.setHours(6, 0, 0, 0);
        break;
      case 'noon':
        newDate.setHours(12, 0, 0, 0);
        break;
      case 'evening':
        newDate.setHours(18, 0, 0, 0);
        break;
      default:
        newDate.setHours(currentTime.getHours(), 0, 0, 0);
    }

    setDateAndTime(newDate);
  }, [dateValue, timeValue]);

  useEffect(() => {
    withApi(async () => {
      if (issuerWalletAddress && validateWalletAddress(issuerWalletAddress)) {
        const wallet = await api.adminWalletsRetrieve({
          address: issuerWalletAddress,
        });
        setDistributionPlatform(wallet.distributionPlatformName);
      } else {
        setDistributionPlatform('');
      }
    });
  }, [issuerWalletAddress]);

  const productTypeOptions = useMemo(
    () =>
      Object.values(ProductTypeEnum).map((value) => ({
        value,
        label: <Translate name={`productType.${snakeToCamelCaseWithNumbers(value.toLowerCase())}`} />,
      })),
    [],
  );

  const networkTypeOptions = useMemo(
    () =>
      Object.entries(NetworkNameTechnicalEnum).map(([key, value]) => ({
        value,
        label: <Translate name={`addProduct.networkType.${key}`} />,
      })),
    [],
  );

  const timeOptions = time.map((value) => ({
    value,
    label: <Translate name={`addProduct.time.${value}`} />,
  }));

  const { getErrorForField: getNetworkError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      invalid: {
        field: 'network',
        translateKey: 'addProduct.fields.network.error',
      },
    },
  });

  const { getErrorForField: getIssuerError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      invalid: {
        field: 'issuer_id',
        translateKey: 'addProduct.fields.issuerId.error',
      },
    },
  });

  const { getErrorForField: getDateError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      invalid: {
        field: 'lock_in_time',
        translateKey: 'addProduct.fields.lockInTime.error',
      },
    },
  });

  const { getErrorForField: getAssetCodeError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      invalid: {
        field: 'asset_code',
        translateKey: 'addProduct.fields.assetCode.error',
      },
    },
  });

  const { getErrorForField: getWalletError } = handleError({
    error,
    translate: useTranslate(),
    specialCodes: {
      wallet_address_unknown: {
        field: 'wallet_address',
        translateKey: 'addProduct.fields.issuerWalletAddress.accountMismatchError',
      },
      wallet_address_wrong_issuer: {
        field: 'wallet_address',
        translateKey: 'addProduct.fields.issuerWalletAddress.accountMismatchError',
      },
      wallet_address_network_mismatch: {
        field: 'wallet_address',
        translateKey: 'addProduct.fields.issuerWalletAddress.networkMismatchError',
      },
      wallet_address_distribution_platform_mismatch: {
        field: 'wallet_address',
        translateKey: 'addProduct.fields.issuerWalletAddress.distributionPlatformMismatchError',
      },
    },
  });

  const isEditView = !!data;

  const searchIssuerName = useCallback((value: string, action?: string) => {
    withApi(async () => {
      if (action === 'menu-close' && value === '') {
        return;
      }
      const issuers = await api.adminIssuersList({
        canCreateProduct: true,
        search: value,
        limit: 10,
      });
      setIssuers(
        issuers.results?.length
          ? issuers.results.map((issuer) => ({
              value: issuer.id,
              label: issuer.companyName,
            }))
          : [],
      );
    });
  }, []);

  useEffect(() => {
    searchIssuerName('');
  }, [searchIssuerName]);

  const debouncedSearch = debounce(searchIssuerName, 500);

  return (
    <Modal onClose={onClose}>
      <Form
        i18nKey="addProduct"
        onSubmit={(values: StateValues<AddProductFields>) => {
          onSubmit(values, dateAndTime);
        }}
        onChange={(values: StateValues<AddProductFields>) => {
          setIsEwpg(values.ewpg === 'true');
          setProvideNominalValue(values.provideNominalValue === 'true');
          setSelectedNetwork(values.network);
          setIssuerWalletAddress(values.issuerWalletAddress);
        }}
        error={error}
        initial={
          data
            ? {
                issuerId: data.issuer?.id,
                issuerName: data.issuer?.person?.companyName,
                issuerWalletAddress:
                  data?.network && data.issuerWallet
                    ? getWalletAddressForNetworkAssertExistence(getNetworkType(data?.network), data.issuerWallet)
                    : '',
                name: data.name,
                productType: data.productType,
                ewpg: data.ewpg?.toString(),
                publicationRestricted: data.publicationRestricted?.toString(),
                isin: data.isin || undefined,
                network: data.network || NetworkNameTechnicalEnum.ETH_DEV,
                assetCode: data.code,
                calculationFactor: data.calculationFactor,
                backupWallet: data.backupWallet,
                nominalValue: data.nominalValue ? toNumber(data.nominalValue) : data.nominalValue,
                issuancePrice: data.issuancePrice ? toNumber(data.issuancePrice) : data.issuancePrice,
                agioPercent: data.agioPercent,
                stepSize: data.stepSize,
                maxIssuableAmount: data.maxIssuableAmount,
                paymentDeadline: data.paymentDeadline,
                cancellationDeadline: data.cancellationDeadline,
                lockInTime: data.lockInTime,
                provideNominalValue: data.provideNominalValue?.toString(),
              }
            : {
                ewpg: 'false',
                publicationRestricted: 'false',
                provideNominalValue: 'true',
              }
        }
      >
        <ModalHeader>{modalHeader}</ModalHeader>
        <ModalContent>
          {isInfoView && <AddProductInfo />}
          {isExitingView && <AddProductExit />}
          {isSuccessView && <AddProductSuccess isEwpg={isEwpg} />}
          {!isInfoView && !isExitingView && !isSuccessView && (
            <Form.ValueProvider>
              {({ name }) => (
                <>
                  {(canEditProduct || !isEditView) && (
                    <Section spacing="regular">
                      <Header spacing="medium">
                        <Translate name="addProduct.issuerSubtitle" />
                      </Header>
                      {isEditView ? (
                        <Form.Group name="issuerName" occupyTooltipSpace={true}>
                          <Form.Input disabled />
                        </Form.Group>
                      ) : (
                        <Form.Group
                          name="issuerId"
                          fieldError={getIssuerError('issuer_id')}
                          required={true}
                          occupyTooltipSpace={true}
                        >
                          <Form.Select options={issuers} onInputChange={debouncedSearch}></Form.Select>
                        </Form.Group>
                      )}
                      <Form.Group
                        name="issuerWalletAddress"
                        required={true}
                        fieldError={getWalletError('wallet_address')}
                        tooltip={
                          <Tooltip
                            content={<Translate name="addProduct.fields.issuerWalletAddress.tooltip" />}
                            position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                          >
                            <Icon name="question" circular={true} size="large" padded="none" color="info" />
                          </Tooltip>
                        }
                      >
                        <Form.Input />
                        {selectedNetwork && isStellarNetwork(selectedNetwork) && Form.Validators.StellarAddress()}
                        {selectedNetwork && !isStellarNetwork(selectedNetwork) && Form.Validators.EthereumAddress()}
                        {selectedNetwork &&
                          !isStellarNetwork(selectedNetwork) &&
                          Form.Validators.TokenAddressChecksum()}
                        {!selectedNetwork && Form.Validators.WalletAddress()}
                      </Form.Group>
                    </Section>
                  )}
                  <Section spacing="regular">
                    <Header spacing="medium">
                      <Translate name="addProduct.productSubtitle" />
                    </Header>
                    <Form.Group
                      name="name"
                      required={true}
                      tooltip={
                        <Tooltip
                          content={<Translate name="addProduct.fields.name.tooltip" />}
                          position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                        >
                          <Icon name="question" circular={true} size="large" padded="none" color="info" />
                        </Tooltip>
                      }
                    >
                      <Form.Input disabled={isProductValidated || isProductUnderValidation} />
                    </Form.Group>
                    {!isProductValidated && !isProductUnderValidation && (
                      <>
                        <Form.Group
                          name="productType"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.productType.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Select options={productTypeOptions}></Form.Select>
                        </Form.Group>
                        <Section spacing="small">
                          <Styled.RadioContainer>
                            <Styled.LabelRadio size="tiny">
                              <Translate name="addProduct.fields.ewpg.label" />
                            </Styled.LabelRadio>
                            <Form.Group name="ewpg" required={true} labelSpacing="none" spacing="none" label={null}>
                              <Form.GroupToggle>
                                <Form.Radio value="true" key="true" disabled={isEditView}>
                                  <Translate name="commonOptions.true" />
                                </Form.Radio>
                                <Form.Radio value="false" key="false" disabled={isEditView}>
                                  <Translate name="commonOptions.false" />
                                </Form.Radio>
                              </Form.GroupToggle>
                            </Form.Group>
                          </Styled.RadioContainer>
                        </Section>
                        {isEwpg && (
                          <Section spacing="small">
                            <Styled.RadioContainer>
                              <Styled.LabelRadio size="tiny">
                                <Translate name="addProduct.fields.publicationRestricted.label" />
                              </Styled.LabelRadio>
                              <Form.Group
                                name="publicationRestricted"
                                required={isEwpg}
                                labelSpacing="none"
                                spacing="none"
                                label={null}
                                forceLabelOnNewLine={true}
                                tooltip={
                                  <Tooltip
                                    content={<Translate name="addProduct.fields.publicationRestricted.tooltip" />}
                                    position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                                  >
                                    <Icon name="question" circular={true} size="large" padded="none" color="info" />
                                  </Tooltip>
                                }
                              >
                                <Form.GroupToggle>
                                  <Form.Radio value="true" key="true">
                                    <Translate name="commonOptions.true" />
                                  </Form.Radio>
                                  <Form.Radio value="false" key="false">
                                    <Translate name="commonOptions.false" />
                                  </Form.Radio>
                                </Form.GroupToggle>
                              </Form.Group>
                            </Styled.RadioContainer>
                          </Section>
                        )}
                        <Form.Group
                          name="isin"
                          required={isEwpg}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.isin.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input />
                        </Form.Group>
                      </>
                    )}
                  </Section>
                  {!isProductValidated && !isProductUnderValidation && (
                    <>
                      <Section spacing="regular">
                        <Header spacing="medium">
                          <Translate name="addProduct.tokenizationInformationSubtitle" />
                        </Header>
                        <Form.Group
                          name="network"
                          required={true}
                          fieldError={getNetworkError('network')}
                          occupyTooltipSpace={true}
                        >
                          <Form.Select options={networkTypeOptions}></Form.Select>
                        </Form.Group>
                        <Form.Group
                          name="assetCode"
                          required={true}
                          fieldError={getAssetCodeError('asset_code')}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.assetCode.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input maxLength={10} />
                        </Form.Group>
                        <Form.Group
                          name="calculationFactor"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.calculationFactor.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" />
                        </Form.Group>
                        <Form.Group
                          name="backupWallet"
                          required={!isEwpg}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.backupWallet.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          {!isEwpg &&
                            selectedNetwork &&
                            isStellarNetwork(selectedNetwork) &&
                            Form.Validators.StellarAddress()}
                          {!isEwpg &&
                            selectedNetwork &&
                            !isStellarNetwork(selectedNetwork) &&
                            Form.Validators.EthereumAddress()}
                          {!isEwpg &&
                            selectedNetwork &&
                            !isStellarNetwork(selectedNetwork) &&
                            Form.Validators.TokenAddressChecksum()}
                          {!isEwpg && !selectedNetwork && Form.Validators.WalletAddress()}
                          <Form.Input disabled={isEwpg} />
                        </Form.Group>
                      </Section>
                      <Section spacing="regular">
                        <Form.Group
                          name="lockInTime"
                          fieldError={getDateError('lock_in_time')}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.lockInTime.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Styled.Row>
                            <Styled.Column>
                              <DatePicker
                                placeholder={translate('addProduct.fields.date.label')}
                                value={dateValue}
                                minDate={new Date()}
                                onChange={(value: any) => {
                                  setDateValue(value);
                                }}
                              />
                            </Styled.Column>
                            <Styled.Column>
                              <SelectAtom
                                placeholder={translate('addProduct.fields.time.label')}
                                options={timeOptions}
                                value={timeValue}
                                onChange={(value: string) => {
                                  setTimeValue(value);
                                }}
                              ></SelectAtom>
                            </Styled.Column>
                          </Styled.Row>
                        </Form.Group>
                      </Section>
                    </>
                  )}
                  {!isProductValidated && !isProductUnderValidation && (
                    <>
                      <Section spacing="small">
                        <Header spacing="medium">
                          <Translate name="addProduct.investmentInformationSubtitle" />
                        </Header>
                        <Section spacing="small">
                          <Styled.RadioContainer>
                            <Styled.LabelRadio size="tiny">
                              <Translate name="addProduct.fields.provideNominalValue.label" />
                            </Styled.LabelRadio>
                            <Form.Group
                              name="provideNominalValue"
                              labelSpacing="none"
                              spacing="none"
                              label={null}
                              forceLabelOnNewLine={true}
                              required={true}
                            >
                              <Form.GroupToggle>
                                <Form.Radio value="true" key="true">
                                  <Translate name="commonOptions.true" />
                                </Form.Radio>
                                <Form.Radio value="false" key="false">
                                  <Translate name="commonOptions.false" />
                                </Form.Radio>
                              </Form.GroupToggle>
                            </Form.Group>
                          </Styled.RadioContainer>
                        </Section>
                        <Form.Group
                          name="nominalValue"
                          required={!!provideNominalValue}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.nominalValue.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} isCurrency disabled={!provideNominalValue} />
                        </Form.Group>
                        <Form.Group
                          name="issuancePrice"
                          required={!provideNominalValue}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.issuancePrice.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} isCurrency />
                        </Form.Group>
                        <Form.Group
                          name="agioPercent"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.agioPercent.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} max={100} isPercentage />
                        </Form.Group>
                        <Form.Group
                          name="stepSize"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.stepSize.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} />
                        </Form.Group>
                        <Form.Group
                          name="maxIssuableAmount"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.maxIssuableAmount.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} />
                        </Form.Group>
                        <Form.Group
                          name="paymentDeadline"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.paymentDeadline.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} />
                        </Form.Group>
                        <Form.Group
                          name="cancellationDeadline"
                          required={true}
                          tooltip={
                            <Tooltip
                              content={<Translate name="addProduct.fields.cancellationDeadline.tooltip" />}
                              position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                            >
                              <Icon name="question" circular={true} size="large" padded="none" color="info" />
                            </Tooltip>
                          }
                        >
                          <Form.Input type="number" min={0} />
                        </Form.Group>
                      </Section>
                    </>
                  )}
                  {((isProductValidated && canEditIssuanceDocuments) ||
                    (!isProductValidated && !isProductUnderValidation && canEditProduct) ||
                    !isEditView) && (
                    <Form.Group
                      name="issuanceDocument"
                      required={!data?.issuanceDocument}
                      tooltip={
                        <Tooltip
                          content={<Translate name="addProduct.fields.issuanceDocument.tooltip" />}
                          position={isPhone ? TooltipPosition.BOTTOM : TooltipPosition.RIGHT}
                        >
                          <Icon name="question" circular={true} size="large" padded="none" color="info" />
                        </Tooltip>
                      }
                    >
                      {uploadedIssuanceDocument && (
                        <FileUploadDocumentContainer
                          uploadedFile={{
                            file: {
                              name: uploadedIssuanceDocument.name,
                              size: uploadedIssuanceDocument.sizeInBytes,
                            } as File,
                          }}
                          onRemoveDocument={
                            isProductUnderValidation ? () => {} : () => setUploadedIssuanceDocument(null)
                          }
                        />
                      )}
                      {!uploadedIssuanceDocument && !isProductUnderValidation && (
                        <>
                          <Form.FileUpload />
                          {isEditView && isProductValidated && (
                            <>
                              <Spacer y={4} />
                              <Form.Group name="changeType" required={true}>
                                <Form.Select
                                  options={[
                                    {
                                      value: ChangeTypeEnum.MAJOR,
                                      label: <Translate name={'dashboardIssuerProductDetails.majorChange'} />,
                                    },
                                    {
                                      value: ChangeTypeEnum.MINOR,
                                      label: <Translate name={'dashboardIssuerProductDetails.minorChange'} />,
                                    },
                                  ]}
                                ></Form.Select>
                              </Form.Group>
                            </>
                          )}
                        </>
                      )}
                    </Form.Group>
                  )}
                  {distributionPlatform && name && !isEditView && (
                    <Hint variant="warning">
                      <Translate
                        as="span"
                        name="addProduct.walletHint"
                        args={[<strong>{distributionPlatform}</strong>, <strong>{name}</strong>]}
                      />
                    </Hint>
                  )}
                </>
              )}
            </Form.ValueProvider>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons actionButtons={actionButtons} />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddProductModal;
